import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm, ClassicFieldMeta, classicFieldErrorMessage } from "./formFieldHelpers";
import TextField from "../../../common/formFields/TextField";

const CASE_NUMBER_MAX_LENGTH = 40;

export interface CaseNumberFieldProps {
    /** Currently entered Case number (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: string;
    /**
     * Whether the field is required - varies by mount site in the classic
     * pane (Details tab: required for [3, 53].includes(requestType); DDS/
     * Requestor tab: always required, since it only mounts there for
     * Disability/Social Security). Computed by the caller, not this
     * component, since the two mount sites have different rules.
     */
    readonly required: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effect the caller wants to run on blur (the
     * classic pane's autosave PATCH - fires onBlur there, not onChange, to
     * avoid a PATCH per keystroke). The base DATA_ENTRY_FIELDS_FORM write
     * happens on every change via this component's own writeFieldToForm.
     */
    readonly onCaseNumberBlur?: (value?: string) => void;
    /**
     * Extra, non-form side effect the caller wants to run on EVERY keystroke
     * (Spec #34102 / F1d bug fix). Quick Entry's own call site uses this to
     * mark the row as actively being edited the instant typing starts - this
     * component's row mapper (mapCaseNumberToFieldGroupRow, below) flips
     * status to Confirmed as soon as the field is non-empty, which for a
     * free-text field happens after the FIRST character, long before the
     * specialist is actually done typing. Without a signal that fires that
     * early, Quick Entry's row would swap out from under the specialist
     * mid-keystroke (isEditing recomputes false: status is now Confirmed and
     * nothing had marked the row as manually-edited yet), unmounting this
     * very input. Classic Mode's own call site doesn't need this - it has no
     * isEditing concept to protect.
     */
    readonly onCaseNumberChange?: (value: string) => void;
    /**
     * Real redux-form validation state, threaded down from
     * DataEntryDetailsRightPane.tsx (Spec #34102 / F1d regression fix -
     * restores "Case Number is required" text on a blocked Next-click).
     * Omitted by Quick Entry's own call site.
     */
    readonly meta?: ClassicFieldMeta;
}

/**
 * Shared Case Number field (Spec #34098 / F1a). The same redux-form field
 * (FormConstants.CASE_NUMBER) is mounted at two different places in the
 * classic RightPane depending on request type - this component doesn't
 * know or care which; the caller decides whether/where to render it and
 * what `required` should be.
 */
const CaseNumberField: React.FC<CaseNumberFieldProps> = (props) => {
    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;

    const onChange = (value: string) => {
        writeFieldToForm(props.dispatch, FormConstants.CASE_NUMBER, value, formName);
        props.onCaseNumberChange?.(value);
    };

    /**
     * Restores ReduxFormTextField's customOnBlur trim behavior (Spec #34102 /
     * F1d regression fix): trim before writing back to the form and before
     * calling the caller's onBlur/autosave, same as every other text field in
     * this app still wrapped in ReduxFormTextField. Applied unconditionally
     * (both Classic Mode and Quick Entry) - normalizing stray whitespace is
     * correct regardless of which UI captured it, and this Spec's own
     * business rules (7-10) don't call for preserving it.
     */
    const onBlur = () => {
        const trimmedValue = props.value?.trim() ?? props.value;
        if (trimmedValue !== props.value) {
            writeFieldToForm(props.dispatch, FormConstants.CASE_NUMBER, trimmedValue, formName);
        }
        props.onCaseNumberBlur?.(trimmedValue);
    };

    return (
        <TextField
            label="Case number"
            placeholder="123456789"
            maxLength={CASE_NUMBER_MAX_LENGTH}
            required={props.required}
            errMsg={classicFieldErrorMessage(props.meta)}
            onChange={onChange}
            onBlur={onBlur}
            value={props.value}
            suppressEnterSubmit
        />
    );
};

export default CaseNumberField;

export const CASE_NUMBER_ROW_ID = "caseNumber";

/**
 * Builds the FieldGroupRow for the Case Number row.
 *
 * `aiValue` is the AI-extracted case number (requestAnalysis.data.caseNumber).
 * DataEntryDetails seeds it into the form field on a Completed extraction, so
 * `formValue` normally already holds it; the row shows the value (Confirmed) and
 * reads as AI-pre-selected (green check) while it still equals the extracted
 * value, flipping to user-entered once the specialist edits it via [Edit].
 */
export function mapCaseNumberToFieldGroupRow(badgeNumber: number, required: boolean, formValue: string | undefined, aiValue?: string): FieldGroupRow {
    const hasValue = Boolean(formValue);
    const isAiPreSelected = Boolean(aiValue) && formValue === aiValue;

    return {
        id: CASE_NUMBER_ROW_ID,
        badgeNumber,
        status: hasValue
            ? FieldGroupRowStatus.Confirmed
            : (required ? FieldGroupRowStatus.NeedsEntry : FieldGroupRowStatus.EmptyNotRequired),
        label: "Case Number",
        value: hasValue ? String(formValue) : null,
        isAiPreSelected,
    };
}
